// Data that comes from somewhere else (GitHub, Changeling's server, this
// browser's storage), as one value that says everything a view may show
// about it:
//
// - `NotAsked`: nothing has asked for it yet.
// - `Loading`: asked for, and nothing to show meanwhile.
// - `Failed`: nothing to show, for `reason`; `retry` says whether it is
//   tried again by itself.
// - `Has`: a value to show, with three things said about it at once:
//   - `freshness`: `current`, or `stale` since a time, with the state of
//     its refresh (`idle`, `running`, or `failed` for a reason, with a
//     retry). Only a stale value has a refresh: a current one needs none.
//   - `gaps`: null, or how many parts did not load, why, and their retry.
//
// So a saved copy whose refresh failed on a rate limit, with matches that
// never loaded, is one value, and nothing contradictory is: no refresh of
// current data, no retry without a reason, no gap of nothing.
//
// A retry is `Scheduled` only when the runtime armed it (runtime/effects.ts),
// and is said to happen by itself only while it is pending. What the user
// is told comes from `statusOf` alone.
import { explain, retriesFor, type Failure } from '../api/failure';
import { clockTime, describeRateLimit, inDuration, shortTime } from '../api/rateLimit';
import { backoffMs } from '../logic/backoff';
import { manual, pendingAt, Scheduled, type Retry } from './effects';

export { manual, Scheduled, type Manual, type Retry } from './effects';

export type Refresh =
  | { readonly kind: 'idle' }
  | { readonly kind: 'running' }
  | { readonly kind: 'failed'; readonly reason: Failure; readonly retry: Retry };

export type Freshness = { readonly kind: 'current' } | { readonly kind: 'stale'; readonly since: number; readonly refresh: Refresh };

/** Parts that did not load: at least one. */
export interface Gaps {
  readonly missing: number;
  readonly reason: Failure;
  readonly retry: Retry;
}

export type Remote<T> =
  | { readonly tag: 'NotAsked' }
  | { readonly tag: 'Loading' }
  | { readonly tag: 'Failed'; readonly reason: Failure; readonly retry: Retry }
  | { readonly tag: 'Has'; readonly value: T; readonly freshness: Freshness; readonly gaps: Gaps | null };

export const notAsked: Remote<never> = Object.freeze({ tag: 'NotAsked' });
export const loading: Remote<never> = Object.freeze({ tag: 'Loading' });
export const failed = (reason: Failure, retry: Retry): Remote<never> => ({ tag: 'Failed', reason, retry });

export const current: Freshness = Object.freeze({ kind: 'current' });
export const idle: Refresh = Object.freeze({ kind: 'idle' });
export const running: Refresh = Object.freeze({ kind: 'running' });
export const refreshFailed = (reason: Failure, retry: Retry): Refresh => ({ kind: 'failed', reason, retry });
export const staleSince = (since: number, refresh: Refresh = idle): Freshness => ({ kind: 'stale', since, refresh });

/** Gaps of `missing` parts; null when nothing is missing. Throws a RangeError for a count that is not a whole number. */
export function gaps(missing: number, reason: Failure, retry: Retry = manual): Gaps | null {
  if (!Number.isInteger(missing) || missing < 0) throw new RangeError('gaps: missing must be a whole number');
  return missing === 0 ? null : { missing, reason, retry };
}

export const has = <T>(value: T, freshness: Freshness = current, g: Gaps | null = null): Remote<T> => ({ tag: 'Has', value, freshness, gaps: g });
/** A current value with nothing missing. */
export const ready = <T>(value: T): Remote<T> => has(value);
/** A value as it was at `since`. */
export const stale = <T>(value: T, since: number, refresh: Refresh = idle): Remote<T> => has(value, staleSince(since, refresh));
/** A current value with parts missing. */
export const partial = <T>(value: T, missing: number, reason: Failure, retry: Retry = manual): Remote<T> => has(value, current, gaps(missing, reason, retry));

/** The value, when there is one. */
export function valueOf<T>(r: Remote<T>): T | undefined {
  return r.tag === 'Has' ? r.value : undefined;
}

export function map<A, B>(r: Remote<A>, f: (a: A) => B): Remote<B> {
  return r.tag === 'Has' ? has(f(r.value), r.freshness, r.gaps) : r;
}

/**
 * `r` with the retry `s` made manual wherever it is: for the `lapsed`
 * message of a retry that was superseded or cancelled (runtime/effects.ts).
 * Other retries stay; `r` itself is returned when `s` is not in it.
 */
export function settle<T>(r: Remote<T>, s: Scheduled): Remote<T> {
  const fix = (x: Retry) => (x === s ? manual : x);
  if (r.tag === 'Failed') return r.retry === s ? failed(r.reason, manual) : r;
  if (r.tag !== 'Has') return r;
  const f = r.freshness;
  const refreshHit = f.kind === 'stale' && f.refresh.kind === 'failed' && f.refresh.retry === s;
  const gapsHit = r.gaps?.retry === s;
  if (!refreshHit && !gapsHit) return r;
  const freshness = refreshHit && f.kind === 'stale' && f.refresh.kind === 'failed' ? staleSince(f.since, refreshFailed(f.refresh.reason, manual)) : f;
  return has(r.value, freshness, r.gaps && { ...r.gaps, retry: fix(r.gaps.retry) });
}

/** The retry of several together: by itself only when every one is pending, then the latest of them; else manual. */
function bothRetry(rs: Retry[]): Retry {
  let latest: Scheduled | null = null;
  for (const r of rs) {
    if (pendingAt(r) === null) return manual;
    if (!latest || (r as Scheduled).at > latest.at) latest = r as Scheduled;
  }
  return latest ?? manual;
}

type Values<Rs extends readonly Remote<unknown>[]> = { -readonly [K in keyof Rs]: Rs[K] extends Remote<infer T> ? T : never };

/**
 * Several remotes as one, holding their values in order. Any `Failed`
 * decides (the first one's reason; retried by itself only when every
 * failed one is, at the latest time), else any `Loading`, else any
 * `NotAsked`. With every value there, each field folds on its own: stale
 * as of the oldest, its refresh failed if any failed (as above), else
 * running if any runs; gaps added up, with the first reason.
 */
export function all<const Rs extends readonly Remote<unknown>[]>(rs: Rs): Remote<Values<Rs>> {
  const fails: { reason: Failure; retry: Retry }[] = [];
  let anyLoading = false;
  let anyNotAsked = false;
  const values: unknown[] = [];
  let since = Infinity;
  const refreshFails: { reason: Failure; retry: Retry }[] = [];
  let anyRunning = false;
  let missing = 0;
  const gapList: Gaps[] = [];
  for (const r of rs) {
    switch (r.tag) {
      case 'Failed':
        fails.push(r);
        break;
      case 'Loading':
        anyLoading = true;
        break;
      case 'NotAsked':
        anyNotAsked = true;
        break;
      case 'Has':
        values.push(r.value);
        if (r.freshness.kind === 'stale') {
          since = Math.min(since, r.freshness.since);
          const f = r.freshness.refresh;
          if (f.kind === 'failed') refreshFails.push(f);
          else if (f.kind === 'running') anyRunning = true;
        }
        if (r.gaps) {
          missing += r.gaps.missing;
          gapList.push(r.gaps);
        }
        break;
    }
  }
  if (fails.length) return failed(fails[0].reason, bothRetry(fails.map((f) => f.retry)));
  if (anyLoading) return loading;
  if (anyNotAsked) return notAsked;
  const refresh: Refresh = refreshFails.length
    ? refreshFailed(refreshFails[0].reason, bothRetry(refreshFails.map((f) => f.retry)))
    : anyRunning
      ? running
      : idle;
  const freshness = since === Infinity ? current : staleSince(since, refresh);
  const g = gapList.length ? { missing, reason: gapList[0].reason, retry: bothRetry(gapList.map((x) => x.retry)) } : null;
  return has(values as Values<Rs>, freshness, g);
}

/** Two remotes as one (see `all`), their values combined by `f`. */
export function merge<A, B, C>(a: Remote<A>, b: Remote<B>, f: (a: A, b: B) => C): Remote<C> {
  return map(all([a, b] as const), ([x, y]) => f(x, y));
}

/**
 * When a load that failed so, after `failures` earlier failed tries, should
 * be tried again by itself (epoch ms): when a rate limit lifts; after a
 * backoff while the failure may pass on its own (`retriesFor` times); else
 * null, only when the user asks. Arm it with the `retry` effect, which
 * hands back the `Scheduled` retry to show.
 */
export function retryAt(f: Failure, failures: number, now = Date.now(), random: () => number = Math.random): number | null {
  if (f.cause === 'rate_limited' && f.rateLimit) return f.rateLimit.until;
  if (failures < retriesFor(f)) return now + backoffMs(failures, random);
  return null;
}

/** What to tell the user about a remote. */
export interface Status {
  /** Work is under way (for `aria-busy`). */
  busy: boolean;
  /** The headline; the same whatever the time. Null: nothing to say. */
  summary: string | null;
  /** Sentences that count down (re-read them as time passes); never for a live region. */
  next: string | null;
  advice: string | null;
  /**
   * What a polite live region says. It does not change as time passes, so
   * it is announced once per state, not once per tick.
   */
  announce: string | null;
}

const NOTHING: Status = Object.freeze({ busy: false, summary: null, next: null, advice: null, announce: null });

const capitalized = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** The countdown to a pending retry. */
function scheduledNext(at: number, now: number): string {
  return at <= now ? 'Trying again now…' : `Changeling will try again by itself at ${clockTime(at)}, in ${inDuration(at - now)}.`;
}

/** The time-stable promise of a pending retry, for the live region. */
const promise = (at: number) => `Changeling will try again by itself at ${clockTime(at)}.`;

interface Words {
  summary: string;
  next: string | null;
  advice: string | null;
  /** The pending retry's time, when there is one. */
  at: number | null;
}

/** A failure's words for loading `what`, given its retry. */
function failureWords(f: Failure, what: string, retry: Retry, now: number): Words {
  const at = pendingAt(retry);
  if (f.cause === 'rate_limited' && f.rateLimit) {
    // describeRateLimit counts down to the limit's end; it may say so only when the retry is then.
    const own = at !== null && at === f.rateLimit.until;
    const w = describeRateLimit(f.rateLimit, now, own);
    return { summary: `Couldn't load ${what}: ${w.summary}`, next: at === null || own ? w.next : scheduledNext(at, now), advice: w.advice, at };
  }
  // `explain` words an offline failure as retried when the connection is back; that is so only when scheduled.
  const e = explain(f, what, now, false);
  const advice = f.cause === 'offline' ? (at === null ? 'Check your connection, then try again.' : 'Check your connection.') : e.advice;
  return { summary: e.summary, next: at === null ? null : scheduledNext(at, now), advice, at };
}

/** When a stale value is as of: the time of day when that was today, with the date before it otherwise. */
function asOf(since: number, now: number): string {
  const then = new Date(since);
  if (then.toDateString() === new Date(now).toDateString()) return shortTime(since);
  return `${then.toLocaleDateString([], { month: 'short', day: 'numeric' })}, ${shortTime(since)}`;
}

const join = (parts: (string | null)[]) => parts.filter((p): p is string => !!p).join(' ') || null;

/**
 * What the user is told about `r`, data named by `what` (a noun phrase:
 * "this section's search"), at `now`. Total over every remote. A retry by
 * itself is promised only for a `Scheduled` retry that is still pending.
 */
export function statusOf(r: Remote<unknown>, what: string, now = Date.now()): Status {
  switch (r.tag) {
    case 'NotAsked':
      return NOTHING;
    case 'Loading': {
      const text = `Loading ${what}…`;
      return { busy: true, summary: text, next: null, advice: null, announce: text };
    }
    case 'Failed': {
      const w = failureWords(r.reason, what, r.retry, now);
      return { busy: false, summary: w.summary, next: w.next, advice: w.advice, announce: join([w.summary, w.at === null ? null : promise(w.at)]) };
    }
    case 'Has': {
      const f = r.freshness;
      if (f.kind === 'current' && !r.gaps) return NOTHING;
      const parts: Words[] = [];
      let lead: string | null = null;
      if (f.kind === 'stale') {
        const as = `Showing ${what} as of ${asOf(f.since, now)}`;
        if (f.refresh.kind === 'failed') {
          lead = `${as}.`;
          parts.push(failureWords(f.refresh.reason, `changes to ${what}`, f.refresh.retry, now));
        } else lead = f.refresh.kind === 'running' ? `${as}; checking for changes…` : `${as}.`;
      }
      if (r.gaps) {
        const w = failureWords(r.gaps.reason, 'the rest', r.gaps.retry, now);
        const head = f.kind === 'stale' ? `Not all of it loaded (${r.gaps.missing} missing).` : `${capitalized(what)} loaded only in part (${r.gaps.missing} missing).`;
        parts.push({ ...w, summary: `${head} ${w.summary}` });
      }
      const summary = join([lead, ...parts.map((p) => p.summary)])!;
      return {
        busy: f.kind === 'stale' && f.refresh.kind === 'running',
        summary,
        next: join(parts.map((p) => p.next)),
        advice: parts.find((p) => p.advice)?.advice ?? null,
        announce: join([summary, ...parts.map((p) => (p.at === null ? null : promise(p.at)))]),
      };
    }
  }
}
